/**
 * JalaliDatePicker — shared date / datetime picker for the form engine.
 *
 * Persian-first: displays the Jalali (شمسی) calendar with Persian digits by
 * default, or Gregorian when the field's `type_config.calendar_mode` is
 * 'gregorian'.
 *
 * ── Storage invariant ──────────────────────────────────────────────────────
 * The VALUE persisted into `submissions.field_values` is ALWAYS a Gregorian
 * ISO string — 'YYYY-MM-DD' for date, 'YYYY-MM-DDTHH:mm' for datetime — exactly
 * what the old native inputs stored. The validation engine compares these
 * strings lexicographically, and the rule editor authors min_date/max_date
 * with native Gregorian inputs, so keeping Gregorian storage is what makes
 * everything line up. Only the *display* is Jalali.
 *
 * The 'T' separator is handled manually (not via date-object format tokens) so
 * the round-trip is exact regardless of calendar.
 */
import { useMemo } from 'react'
import DatePicker, { DateObject } from 'react-multi-date-picker'
import TimePicker from 'react-multi-date-picker/plugins/time_picker'
import 'react-multi-date-picker/styles/backgrounds/bg-dark.css'
import 'react-multi-date-picker/styles/colors/teal.css'
import { cn } from '@/lib/utils/cn'
import {
  parseStored,
  serializeStored,
  displayCalendarFor,
  displayLocaleFor,
  type CalendarMode,
} from './jalali-date-utils'

export type JalaliPickerMode = 'date' | 'datetime'

interface JalaliDatePickerProps {
  /** Stored Gregorian ISO string: 'YYYY-MM-DD' or 'YYYY-MM-DDTHH:mm'. */
  value: string
  onChange: (val: string) => void
  onBlur?: () => void
  disabled?: boolean
  invalid?: boolean
  mode: JalaliPickerMode
  calendarMode?: CalendarMode
}

const inputClass =
  'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ' +
  'ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none ' +
  'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ' +
  'disabled:cursor-not-allowed disabled:opacity-50 md:text-sm'

export function JalaliDatePicker({
  value, onChange, onBlur, disabled, invalid, mode, calendarMode = 'jalali',
}: JalaliDatePickerProps) {
  const displayCalendar = displayCalendarFor(calendarMode)
  const displayLocale = displayLocaleFor(calendarMode)
  const isDateTime = mode === 'datetime'

  // Parse the stored Gregorian string into a DateObject in the display calendar.
  const dateValue = useMemo(
    () => parseStored(value, isDateTime, displayCalendar, displayLocale),
    [value, isDateTime, displayCalendar, displayLocale],
  )

  const handleChange = (d: DateObject | DateObject[] | null) => {
    onChange(serializeStored(Array.isArray(d) ? d[0] ?? null : d, isDateTime))
  }

  return (
    <DatePicker
      value={dateValue}
      onChange={handleChange}
      onClose={onBlur}
      calendar={displayCalendar}
      locale={displayLocale}
      disabled={disabled}
      format={isDateTime ? 'YYYY/MM/DD HH:mm' : 'YYYY/MM/DD'}
      calendarPosition="bottom-right"
      portal
      className="bg-dark teal"
      inputClass={cn(inputClass, invalid && 'border-destructive')}
      plugins={isDateTime ? [<TimePicker key="tp" position="bottom" hideSeconds />] : []}
    />
  )
}
